import { useEffect, useState, useCallback } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { CgEye, CgPen, CgTrash } from "react-icons/cg";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const TestsList = () => {
  const router = useNavigate();
  const [tests, setTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteIds, setDeleteIds] = useState([]);
  const [snack, setSnack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);



  // Fetch tests
  const getTests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(
        `/admin/test?page=${pageNo}&limit=${limit}&search=${encodeURIComponent(query.trim())}`,
        { method: "GET" }
      );
      const data = response.data;
      if (data.s === 200) {
        setTests(data.r || []);
        setFilteredTests(data.r || []);
        setTotalRecords(data.tr || 0);
      }
    } catch (err) {
      console.error("Failed to fetch tests:", err);
      setSnack({
        notification: { message: "Error fetching tests", type: "error" },
      });
      setTests([]);
      setFilteredTests([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [pageNo, limit, query]);

  useEffect(() => {
    getTests();
  }, [getTests]);

  useEffect(() => {
    setPageNo(1);
  }, [query]);

  const totalPages = Math.ceil(totalRecords / limit) || 1;
  const paginatedTests = filteredTests;
  const visibleIds = paginatedTests.map((test) => test._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  // Add Test
  const addTest = () => {
    router("/tests/add");
  };

  // View Test
  const viewTest = (id) => {
    router(`/tests/view/${id}`);
  };

  // Edit Test
  const editTest = (id) => {
    router(`/tests/edit/${id}`);
  };

  const toggleTestStatus = async (test) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/test/${test._id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: !test.isActive }),
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        setSnack({
          notification: { message: "Test status updated", type: "success" },
        });
        getTests();
      }
    } catch (err) {
      setSnack({
        notification: { message: "Error updating test status", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  const runBulkAction = async (action) => {
    if (!selectedIds.length) {
      setSnack({
        notification: { message: "Select at least one test", type: "error" },
      });
      return;
    }

    if (action === "delete") {
      setDeleteId(null);
      setDeleteIds(selectedIds);
      setDeleteModel(true);
      return;
    }

    try {
      setLoading(true);
      await Promise.all(
        selectedIds.map((id) => {
          const test = tests.find((item) => item._id === id);
          if (test?.isActive === (action === "activate")) return Promise.resolve();
          return AuthInterceptor(`/admin/test/${id}/status`, {
            method: "PATCH",
            body: JSON.stringify({ isActive: action === "activate" }),
          });
        })
      );
      setSnack({
        notification: { message: "Bulk action completed", type: "success" },
      });
      setSelectedIds([]);
      getTests();
    } catch (error) {
      setSnack({
        notification: { message: "Bulk action failed", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  };


  // Delete Test
  const handleDeleteTest = async () => {
    const ids = deleteIds.length ? deleteIds : deleteId ? [deleteId] : [];
    if (!ids.length) {
      setDeleteModel(false);
      return;
    }

    try {
      setLoading(true);
      const response = await AuthInterceptor("/admin/test", {
        method: "DELETE",
        body: JSON.stringify({ ids }),
      });
      const data = response.data;

      if (data.s === 200) {
        setSnack({
          notification: {
            message: data.m || "Test deleted successfully",
            type: "success",
          },
        });
        setDeleteModel(false);
        setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        const remainingRecords = Math.max(totalRecords - ids.length, 0);
        const lastPage = Math.ceil(remainingRecords / limit) || 1;
        if (pageNo > lastPage) {
          setPageNo(lastPage);
        } else {
          getTests();
        }
      } else {
        throw new Error(data.m || "Error deleting test");
      }
    } catch (err) {
      console.error("Error deleting test:", err);
      setSnack({
        notification: { message: err.message, type: "error" },
      });
    } finally {
      setLoading(false);
      setDeleteId(null);
      setDeleteIds([]);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h2 className="mb-2 mb-md-0">Tests</h2>
          <select
            className="form-select form-select-sm w-auto"
            value={limit}
            onChange={(e) => {
              setPageNo(1);
              setLimit(Number(e.target.value));
            }}
          >
            {[5, 10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>

        {/* Search + Add */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <input
              className="form-control"
              style={{ width: "40vw" }}
              placeholder="Search tests..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button
            className="btn btn-outline-custom"
            onClick={() => addTest(true)}
          >
            Add Test
          </button>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="text-muted small">{selectedIds.length} selected</span>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("activate")}>
            Active
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("inactive")}>
            Inactive
          </button>
          <button className="btn btn-sm btn-outline-danger" onClick={() => runBulkAction("delete")}>
            Delete
          </button>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all tests on this page"
                  />
                </th>
                <th>Sr. No.</th>
                <th>Title</th>
                <th>Age Limit</th>
                <th>Report</th>
                <th>Status</th>
                <th style={{ width: "150px" }} className="text-start">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedTests.length > 0 ? (
                paginatedTests.map((t, idx) => (
                  <tr key={t._id || idx}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(t._id)}
                        onChange={() => toggleRowSelection(t._id)}
                        aria-label={`Select ${t.title || "test"}`}
                      />
                    </td>
                    <td>{(pageNo - 1) * limit + idx + 1}</td>
                    <td>{t.title}</td>
                    <td>{t.ageLimit}</td>
                    <td>{t.report}</td>
                    <td>
                      {t.isActive ? (
                        <span className="badge bg-success" role="button" onClick={() => toggleTestStatus(t)}>Active</span>
                      ) : (
                        <span className="badge bg-secondary" role="button" onClick={() => toggleTestStatus(t)}>Inactive</span>
                      )}
                    </td>
                    <td className="text-start">
                      <button
                        className="btn btn-sm btn-outline-info me-2"
                        onClick={() => viewTest(t._id)}
                      >
                        <CgEye size={16} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-custom me-2"
                        onClick={() => editTest(t._id)}
                      >
                        <CgPen size={16} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                          setDeleteId(t._id);
                          setDeleteIds([]);
                          setDeleteModel(true);
                        }}
                      >
                        <CgTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">
                    No tests found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
            disabled={pageNo === 1}
          >
            Prev
          </button>
          <span>
            Page {pageNo} of {totalPages}
          </span>
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => setPageNo((prev) => Math.min(prev + 1, totalPages))}
            disabled={pageNo >= totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Delete Modal */}
      <Modal show={deleteModel} onHide={() => setDeleteModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Test</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This action can not be reversed. Are you sure you want to delete{" "}
            <strong>{deleteIds.length || 1}</strong> test
            {(deleteIds.length || 1) > 1 ? "s" : ""}?
          </p>
          <p className="text-muted small mb-3">
            The selected test data will be permanently deleted.
          </p>
          <div className="text-end">
            <button
              className="btn btn-outline-secondary me-2"
              onClick={() => {
                setDeleteModel(false);
                setDeleteId(null);
                setDeleteIds([]);
              }}
            >
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDeleteTest}>
              Delete
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TestsList;
